/*
 * SmsReportDateRange — date filter for the SMS & WhatsApp report.
 *
 * Replaces the old dropdown with a react-date-range picker (same pattern as
 * early_retention_checklist/DateRangeFilter). The old dropdown options live on
 * as quick filters in the picker sidebar: clicking one applies immediately and
 * keeps its label as the filter type (so load_data's timezone-aware helper
 * still computes the window). Selecting dates on the calendar becomes a
 * CUSTOM range, committed with the Apply button.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FaRegCalendarAlt, FaChevronDown } from 'react-icons/fa';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import moment from 'moment';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// Quick filters. `type` is a get_date_filter_data (util_functions.js) label —
// when present we emit it and the tz-aware helper computes the window. Presets
// without a `type` (future ranges the helper doesn't know) emit CUSTOM with the
// computed start/end dates instead. Past presets for the Sent tab, future
// presets for the Scheduled tab (scheduled sends are upcoming).
const PAST_PRESETS = [
  {
    label: 'TODAY',
    type: 'TODAY',
    range: () => ({
      startDate: moment().startOf('day').toDate(),
      endDate: moment().endOf('day').toDate()
    })
  },
  {
    label: 'YESTERDAY',
    type: 'YESTERDAY',
    range: () => ({
      startDate: moment().subtract(1, 'days').startOf('day').toDate(),
      endDate: moment().subtract(1, 'days').endOf('day').toDate()
    })
  },
  {
    label: 'THIS WEEK',
    type: 'THIS WEEK',
    range: () => ({
      startDate: moment().startOf('isoWeek').toDate(),
      endDate: moment().endOf('day').toDate()
    })
  },
  {
    label: 'LAST WEEK',
    type: 'LAST WEEK',
    range: () => ({
      startDate: moment().startOf('isoWeek').subtract(1, 'weeks').toDate(),
      endDate: moment().startOf('isoWeek').subtract(1, 'days').endOf('day').toDate()
    })
  },
  {
    label: 'LAST 2 WEEKS',
    type: 'LAST 2 WEEKS',
    range: () => ({
      startDate: moment().subtract(2, 'weeks').startOf('day').toDate(),
      endDate: moment().endOf('day').toDate()
    })
  },
  {
    label: 'LAST 4 WEEKS',
    type: 'LAST 4 WEEKS',
    range: () => ({
      startDate: moment().subtract(4, 'weeks').startOf('day').toDate(),
      endDate: moment().endOf('day').toDate()
    })
  },
  {
    label: 'THIS MONTH',
    type: 'THIS MONTH',
    range: () => ({
      startDate: moment().startOf('month').toDate(),
      endDate: moment().endOf('day').toDate()
    })
  },
  {
    label: 'LAST MONTH',
    type: 'LAST MONTH',
    range: () => ({
      startDate: moment().subtract(1, 'months').startOf('month').toDate(),
      endDate: moment().subtract(1, 'months').endOf('month').toDate()
    })
  },
  {
    label: 'LAST 30 DAYS',
    type: 'LAST 30 DAYS',
    range: () => ({
      startDate: moment().subtract(30, 'days').startOf('day').toDate(),
      endDate: moment().endOf('day').toDate()
    })
  }
];

const FUTURE_PRESETS = [
  {
    label: 'TODAY',
    type: 'TODAY',
    range: () => ({
      startDate: moment().startOf('day').toDate(),
      endDate: moment().endOf('day').toDate()
    })
  },
  {
    label: 'TOMORROW',
    type: 'TOMORROW',
    range: () => ({
      startDate: moment().add(1, 'days').startOf('day').toDate(),
      endDate: moment().add(1, 'days').endOf('day').toDate()
    })
  },
  {
    label: 'THIS WEEK',
    type: 'THIS WEEK',
    range: () => ({
      startDate: moment().startOf('day').toDate(),
      endDate: moment().endOf('isoWeek').toDate()
    })
  },
  {
    label: 'NEXT WEEK',
    type: 'NEXT WEEK',
    range: () => ({
      startDate: moment().add(1, 'weeks').startOf('isoWeek').toDate(),
      endDate: moment().add(1, 'weeks').endOf('isoWeek').toDate()
    })
  },
  {
    // No helper type — emitted as CUSTOM with the dates below.
    label: 'NEXT 2 WEEKS',
    range: () => ({
      startDate: moment().startOf('day').toDate(),
      endDate: moment().add(2, 'weeks').endOf('day').toDate()
    })
  },
  {
    label: 'NEXT 30 DAYS',
    range: () => ({
      startDate: moment().startOf('day').toDate(),
      endDate: moment().add(30, 'days').endOf('day').toDate()
    })
  }
];

const presetsFor = (mode) => (mode === 'future' ? FUTURE_PRESETS : PAST_PRESETS);

// Emit shape consumed by the parent's apply_date_range.
const emitFor = (preset) =>
  preset.type
    ? { type: preset.type, start_date: null, end_date: null }
    : {
        type: 'CUSTOM',
        start_date: moment(preset.range().startDate).format('YYYY-MM-DD'),
        end_date: moment(preset.range().endDate).format('YYYY-MM-DD')
      };

const matchPreset = (mode, startDate, endDate) =>
  presetsFor(mode).find((p) => {
    const r = p.range();
    return moment(startDate).isSame(r.startDate, 'day') && moment(endDate).isSame(r.endDate, 'day');
  }) || null;

const currentRange = (mode, date_filter_type, start_date, end_date) => {
  if (date_filter_type && date_filter_type !== 'CUSTOM') {
    const preset = presetsFor(mode).find((p) => p.type === date_filter_type);
    if (preset) return preset.range();
  }
  return {
    startDate: start_date ? new Date(start_date) : new Date(),
    endDate: end_date ? new Date(end_date) : new Date()
  };
};

function SmsReportDateRange({ mode, date_filter_type, start_date, end_date, on_apply }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [tempRange, setTempRange] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const handleClick = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPop = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inRoot && !inPop) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setTempRange(null);
  };

  const applied = currentRange(mode, date_filter_type, start_date, end_date);
  const shown = tempRange || applied;
  const range = [{ startDate: shown.startDate, endDate: shown.endDate, key: 'selection' }];

  const handleSelect = (item) => {
    const { startDate, endDate } = item.selection;
    const preset = matchPreset(mode, startDate, endDate);
    if (preset) {
      // Quick filter click (or a manual selection that equals one): apply it.
      on_apply(emitFor(preset));
      close();
      return;
    }
    setTempRange({ startDate, endDate });
  };

  const apply_custom = () => {
    if (!tempRange) {
      close();
      return;
    }
    on_apply({
      type: 'CUSTOM',
      start_date: moment(tempRange.startDate).format('YYYY-MM-DD'),
      end_date: moment(tempRange.endDate).format('YYYY-MM-DD')
    });
    close();
  };

  const label =
    date_filter_type && date_filter_type !== 'CUSTOM'
      ? date_filter_type
      : `${moment(applied.startDate).format('DD MMM YYYY')} → ${moment(applied.endDate).format(
          'DD MMM YYYY'
        )}`;

  return (
    <div ref={rootRef} style={{ position: 'relative' }} className="inline-flex items-center gap-2">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          borderRadius: 8,
          fontSize: 13,
          cursor: 'pointer',
          border: '1px solid var(--input-border)',
          background: 'var(--input-bg)',
          color: 'var(--text-primary)'
        }}
      >
        <FaRegCalendarAlt size={12} style={{ color: 'var(--text-muted)' }} />
        <span>{label}</span>
        <FaChevronDown size={11} style={{ color: 'var(--text-muted)' }} />
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="srv3-rdr-scope"
              style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 10000 }}
            >
              <style>{`
                .srv3-rdr-scope .rdrStaticRanges { background: var(--surface-card); }
                .srv3-rdr-scope .rdrStaticRange {
                  background: var(--surface-card);
                  border-bottom-color: var(--border-default);
                }
                .srv3-rdr-scope .rdrStaticRangeLabel { color: var(--text-primary); }
                .srv3-rdr-scope .rdrStaticRange:hover .rdrStaticRangeLabel,
                .srv3-rdr-scope .rdrStaticRange:focus .rdrStaticRangeLabel {
                  background: var(--surface-sunken);
                }
                /* Hide the month/year dropdowns; navigate via the arrows only. */
                .srv3-rdr-scope .rdrMonthAndYearPickers { display: none; }
                /* Compact calendar: default width, slightly reduced vertical spacing. */
                .srv3-rdr-scope .rdrMonth { padding: 0 0.833em 0.8em; }
                .srv3-rdr-scope .rdrMonthName { padding: 0.4em 0.833em; }
                .srv3-rdr-scope .rdrWeekDay { line-height: 1.8em; }
                .srv3-rdr-scope .rdrDay { height: 2.1em; line-height: 2.1em; }
                .srv3-rdr-scope .rdrMonthAndYearWrapper { height: 40px; padding-top: 4px; }
                .srv3-rdr-scope .rdrDefinedRangesWrapper { width: 180px; }
                .srv3-rdr-scope .rdrStaticRanges { min-width: 120px; }
                .srv3-rdr-scope .rdrStaticRangeLabel { padding: 8px 14px; }
              `}</style>
              <div
                style={{
                  overflow: 'hidden',
                  borderRadius: 8,
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-modal)'
                }}
              >
                <DateRangePicker
                  ranges={range}
                  onChange={handleSelect}
                  moveRangeOnFirstSelection={false}
                  editableDateInputs
                  rangeColors={['#ee731b']}
                  showDateDisplay={false}
                  months={2}
                  direction="horizontal"
                  inputRanges={[]}
                  staticRanges={createStaticRanges(presetsFor(mode))}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                    padding: '8px 12px',
                    borderTop: '1px solid var(--border-default)'
                  }}
                >
                  <button
                    type="button"
                    onClick={close}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '4px 10px',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: 'var(--text-muted)'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={apply_custom}
                    disabled={!tempRange}
                    style={{
                      background: tempRange ? 'var(--brand-primary)' : 'var(--border-default)',
                      border: 'none',
                      borderRadius: 6,
                      padding: '4px 14px',
                      cursor: tempRange ? 'pointer' : 'not-allowed',
                      fontSize: 13,
                      color: '#fff'
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

SmsReportDateRange.propTypes = {
  mode: PropTypes.oneOf(['past', 'future']),
  date_filter_type: PropTypes.string,
  start_date: PropTypes.string,
  end_date: PropTypes.string,
  on_apply: PropTypes.func.isRequired
};

SmsReportDateRange.defaultProps = {
  mode: 'past',
  date_filter_type: 'THIS WEEK',
  start_date: '',
  end_date: ''
};

export default SmsReportDateRange;
